import { useState, useEffect } from 'react';
import { Download, Share, X } from 'lucide-react';
import { Button } from '@/components/ui/button.js';

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    // Check if user previously dismissed prompt
    try {
      const dismissed = localStorage.getItem('tsos_pwa_prompt_dismissed');
      if (dismissed) return;
    } catch {}

    // Check if already in standalone mode
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
    if (isStandalone) return;

    // Detect iOS Safari
    const ua = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(ua);
    const isSafari = /safari/.test(ua) && !/chrome|crios|fxios/.test(ua);

    if (isIosDevice && isSafari) {
      setIsIos(true);
      setShowPrompt(true);
      return;
    }

    // Android / Desktop beforeinstallprompt
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice?.outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    try {
      localStorage.setItem('tsos_pwa_prompt_dismissed', 'true');
    } catch {}
  };

  if (!showPrompt) return null;

  return (
    <aside
      aria-label="Install Application"
      className="fixed top-3 left-4 right-4 md:left-auto md:right-6 md:w-96 z-50 bg-white dark:bg-[#12211F] rounded-xl shadow-2xl border border-brand-border p-3.5 flex items-start space-x-3 animate-in fade-in slide-in-from-top-4 duration-300"
    >
      <div className="h-10 w-10 rounded-lg bg-brand-teal text-white flex-shrink-0 flex items-center justify-center font-bold text-sm shadow">
        TS
      </div>
      <div className="flex-1 min-w-0 pr-1">
        <h4 className="text-xs font-bold text-brand-ink flex items-center space-x-1.5">
          <span>Install Techosolution OS</span>
        </h4>
        <p className="text-[11px] text-brand-muted mt-0.5 leading-snug">
          {isIos ? (
            <span className="flex items-center space-x-1">
              <span>Tap Share</span>
              <Share className="h-3 w-3 inline text-brand" />
              <span>then select <strong>Add to Home Screen</strong></span>
            </span>
          ) : (
            'Install as a native standalone app for fast offline access and quick launch.'
          )}
        </p>
        <div className="flex items-center space-x-2 mt-2">
          {!isIos && deferredPrompt && (
            <Button
              size="sm"
              onClick={handleInstallClick}
              className="h-7 text-xs bg-brand-teal hover:bg-teal-700 text-white font-medium px-3 rounded-lg"
            >
              <Download className="h-3 w-3 mr-1" />
              Install App
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDismiss}
            className="h-7 text-xs text-brand-muted hover:text-brand-ink px-2"
          >
            Not now
          </Button>
        </div>
      </div>
      <button
        onClick={handleDismiss}
        className="p-1 text-brand-muted hover:text-brand-ink rounded min-h-[32px] min-w-[32px] flex items-center justify-center"
        aria-label="Dismiss installation banner"
      >
        <X className="h-4 w-4" />
      </button>
    </aside>
  );
}

export default InstallPrompt;
